import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '../lib/queryKeys';
import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

export interface DashboardMetrics {
  totalStudents: number;
  activeSubscriptions: number;
  monthlyRevenue: number;
  upcomingClasses: number;
  pendingPayments: number;
  latePayments: number;
  recentAttendance: number;
  retentionRate: number;
}

export function useDashboardMetrics() {
  const { data: companyId, isLoading: isLoadingCompany } = useCurrentCompany();

  return useQuery<DashboardMetrics>({
    queryKey: queryKeys.dashboard.metrics(companyId),
    enabled: !!companyId && !isLoadingCompany,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
    retry: 1, // Only retry once to avoid spamming errors
    queryFn: async () => {
      if (!companyId) {
        throw new Error('No company ID available');
      }

      // First, get student role ID
      const { data: studentRole } = await supabase
        .from('system_roles')
        .select('system_role_id')
        .eq('role', 'student')
        .single();

      const studentRoleId = studentRole?.system_role_id;

      // Fetch multiple metrics in parallel
      const [
        studentsResult,
        subscriptionsResult,
        classesResult,
        _paymentsResult,
        attendanceResult,
      ] = await Promise.all([
        // Total students
        studentRoleId
          ? supabase
              .from('user_company_relationships')
              .select('id', { count: 'exact', head: true })
              .eq('company_id', companyId)
              .eq('role_id', studentRoleId)
          : Promise.resolve({ count: 0, error: null }),

        // Active subscriptions
        supabase
          .from('subscription_enrollments')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('status', 'active'),

        // Upcoming classes (next 7 days)
        supabase
          .from('calendar_events')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .gte('start_time', new Date().toISOString())
          .lte('start_time', new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()),

        // Pending payments - payment_records doesn't have company_id directly
        // We'll query this separately after getting enrollment IDs
        Promise.resolve({ count: 0, error: null }),

        // Recent attendance (last 7 days) - need to join with calendar_events to filter by company
        supabase
          .from('class_enrollments')
          .select('id', { count: 'exact', head: true })
          .eq('attended', true)
          .gte('checked_in_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()),
      ]);

      // Get subscription count
      const subscriptionsCount = subscriptionsResult.count || 0;

      // Calculate monthly revenue (simplified - would need proper payment aggregation)
      const monthlyRevenue = 0; // TODO: Implement proper revenue calculation

      // Calculate late payments (payments overdue by more than 3 days)
      // payment_records doesn't have company_id directly - need to query through enrollments
      const threeDaysAgo = new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString();
      
      // Get enrollment IDs for this company
      const { data: enrollments } = await supabase
        .from('subscription_enrollments')
        .select('id')
        .eq('company_id', companyId);
      
      const enrollmentIds = enrollments?.map(e => e.id) || [];
      
      // Query payment_records through enrollments
      const { count: latePaymentsCount } = enrollmentIds.length > 0
        ? await supabase
            .from('payment_records')
            .select('id', { count: 'exact', head: true })
            .in('enrollment_id', enrollmentIds)
            .eq('status', 'pending')
            .lt('billing_date', threeDaysAgo)
        : { count: 0 };
      
      // Also get pending payments count (for the earlier query)
      const { count: pendingPaymentsCount } = enrollmentIds.length > 0
        ? await supabase
            .from('payment_records')
            .select('id', { count: 'exact', head: true })
            .in('enrollment_id', enrollmentIds)
            .eq('status', 'pending')
        : { count: 0 };

      // Calculate retention rate (simplified - would need proper calculation)
      const retentionRate = 0; // TODO: Implement retention calculation

      return {
        totalStudents: studentsResult.count || 0,
        activeSubscriptions: subscriptionsCount,
        monthlyRevenue,
        upcomingClasses: classesResult.count || 0,
        pendingPayments: pendingPaymentsCount || 0,
        latePayments: latePaymentsCount || 0,
        recentAttendance: attendanceResult.count || 0,
        retentionRate,
      };
    },
  });
}
